/**
 * Store-driven screen routing (DESIGN-client §7): `presentation.screen` follows the table phase
 * once the player has left Boot/Tutorial; `handScored` / `matchEnd` cues stash their data for the
 * HandEnd / MatchEnd screens; `displayedScores` tracks the authoritative totals whenever the
 * CueQueue is idle (the count-up owns it while busy).
 */
import type { CueQueue } from '../net/CueQueue';
import { store, type SpadesStore, type Screen } from '../store/store';
import { connectTable } from './runtime';
import { normalizeCode } from '@spades/shared';
import { toast } from './ui';

export function screenForPhase(phase: string): Screen {
  switch (phase) {
    case 'lobby':
      return 'lobby';
    case 'handScored':
      return 'handEnd';
    case 'matchEnd':
      return 'matchEnd';
    default:
      return 'table';
  }
}

/** Where the Boot tap goes: tutorial gating per §6 (`?tutorial=1` forces, `?tutorial=0` skips). */
export function screenAfterBoot(opts: { tutorialDone: boolean; search: string }): Screen {
  const p = new URLSearchParams(opts.search);
  const t = p.get('tutorial');
  if (t === '1') return 'tutorial';
  if (t === '0') return 'lobby';
  const hasCode = !!(p.get('code') && normalizeCode(p.get('code')!)) || p.has('join');
  return !opts.tutorialDone && !hasCode ? 'tutorial' : 'lobby';
}

const GATED: readonly Screen[] = ['boot', 'tutorial'];

/**
 * How long the shell waits for the `handScored` cue before showing the panel anyway.
 *
 * Only a backstop. The phase flips the instant the server scores, but the cue is queued behind the
 * trick sweep and the books filing onto the shelf, which is three to four seconds of animation at
 * real speed — so routing on the phase dropped the panel on top of the score walk it was meant to
 * follow. The cue's end is the real signal. This exists so that a driver with no cue path at all,
 * or a handler that never finishes, still lands the player somewhere rather than leaving them on a
 * table that has stopped moving.
 *
 * Comfortably under the server's six-second hold, because it re-arms itself whenever a cue is in
 * flight or the queue still has work. An earlier eight seconds outlived the hold, so on the one
 * path this exists for it never fired at all.
 */
export const HAND_END_CUE_GRACE_MS = 3000;

/**
 * The least time the scoreboard stays up once it has appeared.
 *
 * The server's hand-end hold is scaled by `TIMER_SCALE` but the score walk is not: at `0.1` the hold
 * is 600ms while the walk is a second or more per line, so the next hand is dealt before the walk
 * has finished. Without a floor here the panel would appear as the walk ended and be gone on the
 * next cue, which is no scoreboard at all. At real time the hold is six seconds and this never
 * binds. A scoreboard nobody can read is not a scoreboard.
 */
export const HAND_END_MIN_DWELL_MS = 1500;

export function installRouting(store: SpadesStore, cueQueue: CueQueue): () => void {
  const offs: Array<() => void> = [];

  // The hand-end panel follows the CUE stream, not `table.phase`.
  //
  // The two run at different speeds. The phase is the server's state and flips the instant a hand
  // is scored; the cues are the animation the player is actually watching, and the scoring cue sits
  // behind the trick sweep and the books filing onto the shelf. The server also races further ahead
  // the lower `TIMER_SCALE` goes, because its hold is scaled and the animation is not. Judging by
  // the phase therefore put the panel up before the walk, and judging by the phase a second time
  // took it down before anyone had read it. Both signals here are cues.
  let handScoredSeen = false;
  /** A hand has been scored and no scoreboard has been shown for it yet. */
  let scoredPending = false;
  let handEndShownAt = 0;
  let handEndTimer: ReturnType<typeof setTimeout> | null = null;
  let handEndCloseTimer: ReturnType<typeof setTimeout> | null = null;

  const clearHandEndWait = (): void => {
    if (handEndTimer) clearTimeout(handEndTimer);
    handEndTimer = null;
  };
  const clearHandEndClose = (): void => {
    if (handEndCloseTimer) clearTimeout(handEndCloseTimer);
    handEndCloseTimer = null;
  };

  const showHandEnd = (): void => {
    clearHandEndWait();
    const st = store.getState();
    if (GATED.includes(st.presentation.screen)) return;
    scoredPending = false;
    if (st.presentation.screen === 'handEnd') return;
    handEndShownAt = Date.now();
    st.setPresentation({ screen: 'handEnd' });
  };

  /** Leave the scoreboard, but never before it has been up long enough to read. */
  const leaveHandEnd = (): void => {
    clearHandEndClose();
    const st = store.getState();
    if (st.presentation.screen !== 'handEnd') return;
    const waited = Date.now() - handEndShownAt;
    if (waited < HAND_END_MIN_DWELL_MS) {
      handEndCloseTimer = setTimeout(leaveHandEnd, HAND_END_MIN_DWELL_MS - waited);
      return;
    }
    const phase = store.getState().table?.phase;
    st.setPresentation({ screen: phase ? screenForPhase(phase) : 'table' });
  };

  const armHandEndWait = (): void => {
    clearHandEndWait();
    handEndTimer = setTimeout(() => {
      handEndTimer = null;
      // Deliberately NOT `phase === 'handScored'`. The hold is scaled and the animation is not, so
      // by the time this fires the server has often moved on — checking the live phase here meant
      // the backstop could never fire either, which is how the panel went missing entirely.
      // `scoredPending` remembers that a hand was scored and nothing has shown it yet.
      if (!scoredPending) return;
      // Something is still animating, or a cue is queued and `onEnd` owns the transition. Look
      // again later rather than cutting in front of it.
      const st = store.getState();
      if (handScoredSeen || st.presentation.cueBusy || cueQueue.pending > 0) {
        armHandEndWait();
        return;
      }
      // Only worth showing if there is something to show, or if the server is visibly still holding
      // on the scored hand. Otherwise this would put an empty "Scoring the hand…" panel over a hand
      // that has already been dealt, which is worse than the missing scoreboard it is standing in for.
      if (!st.presentation.lastHandResult && st.table?.phase !== 'handScored') {
        scoredPending = false;
        return;
      }
      showHandEnd();
    }, HAND_END_CUE_GRACE_MS);
  };

  offs.push(
    store.subscribe(
      (s) => s.table?.phase,
      (phase) => {
        if (phase == null) return;
        const st = store.getState();
        if (GATED.includes(st.presentation.screen)) return;
        const next = screenForPhase(phase);
        if (next === 'handEnd') {
          // Deliberately not routed here. The cue's end is the signal; see `HAND_END_CUE_GRACE_MS`.
          scoredPending = true;
          armHandEndWait();
          return;
        }
        clearHandEndWait();
        // Leaving the scoreboard goes through the dwell, so a hold that expires before the walk has
        // even finished cannot snatch the panel away the moment it appears.
        if (st.presentation.screen === 'handEnd') {
          leaveHandEnd();
          return;
        }
        if (next !== st.presentation.screen) st.setPresentation({ screen: next });
      },
    ),
  );

  offs.push(
    store.subscribe(
      (s) => s.table?.teams,
      (teams) => {
        if (!teams) return;
        const st = store.getState();
        if (st.presentation.cueBusy) return;
        const scores: [number, number] = [teams[0]?.score ?? 0, teams[1]?.score ?? 0];
        const cur = st.presentation.displayedScores;
        if (cur[0] !== scores[0] || cur[1] !== scores[1]) st.setPresentation({ displayedScores: scores });
      },
    ),
  );

  offs.push(
    store.subscribe(
      (s) => s.presentation.cueBusy,
      (busy) => {
        if (busy) return;
        const st = store.getState();
        const teams = st.table?.teams;
        if (!teams) return;
        st.setPresentation({ displayedScores: [teams[0]?.score ?? 0, teams[1]?.score ?? 0] });
      },
    ),
  );

  offs.push(
    cueQueue.onStart((cue) => {
      const st = store.getState();
      if (cue.type === 'handScored') {
        handScoredSeen = true;
        st.setPresentation({ lastHandResult: cue.data as never });
      }
      // The next hand is being dealt on screen, which is the honest moment to take the scoreboard
      // down — the server's phase said so seconds earlier, while the walk was still running.
      if (cue.type === 'dealt') {
        scoredPending = false;
        leaveHandEnd();
      }
      if (cue.type === 'matchEnd') {
        st.setPresentation({ lastMatchResult: cue.data as never });
        // The match is over. The final panel outranks a scoreboard for the hand that ended it, so
        // this one does not wait out the dwell.
        scoredPending = false;
        handEndShownAt = 0;
        leaveHandEnd();
      }
      // The server is going down for a deploy. It closes rooms cleanly, so the driver's own
      // reconnect will pick the table back up; the player only needs to be told why the table
      // paused, so they do not think the game crashed and close the tab.
      if (cue.type === 'serverRestarting') {
        toast('Table restarting, hang tight', 'info', 6000);
        rejoinAfterRestart();
      }

      // `lastHandResult` is deliberately NOT cleared on `dealt`.
      //
      // The server deals the next hand while the HandEnd overlay is still up: in a real match the
      // cues arrive as handScored(seq 132) then dealt(seq 133), back to back. Clearing on `dealt`
      // therefore wiped the scoreboard a beat after it filled, and the player saw em-dashes and a
      // 0-0 total for a hand they had just played. Nothing shows a stale result either, because
      // HandEnd only renders while `screen === 'handEnd'`, and each `handScored` overwrites the
      // previous one.
    }),
  );

  offs.push(
    cueQueue.onEnd((cue) => {
      // The moment the scoreboard is owed: the count-up has finished walking to the new totals, so
      // the panel lands on the number the player just watched arrive rather than over the top of it.
      if (cue.type === 'handScored') {
        handScoredSeen = false;
        // Always safe to show here: the queue runs one cue at a time, so nothing else can have
        // started in the meantime, and the `dealt` that follows takes the panel down again.
        showHandEnd();
      }
    }),
  );

  return () => {
    clearHandEndWait();
    clearHandEndClose();
    offs.forEach((off) => off());
  };
}


/**
 * Re-join after a server restart.
 *
 * Colyseus reconnects on its own when it can, so this is the fallback for the case where the room
 * is gone by the time we come back: wait for the server to finish restarting, then reconnect from
 * scratch. Deliberately a single delayed attempt rather than a retry loop — the driver already
 * owns retrying, and two things reconnecting the same session race each other for the seat.
 */
export const RESTART_REJOIN_DELAY_MS = 4000;

let rejoinTimer: ReturnType<typeof setTimeout> | null = null;

export function rejoinAfterRestart(delayMs = RESTART_REJOIN_DELAY_MS): void {
  if (rejoinTimer) return;
  rejoinTimer = setTimeout(() => {
    rejoinTimer = null;
    const st = store.getState();
    // Only if the driver has not already got us back by itself.
    if (st.session.connection === 'connected' || st.session.connection === 'connecting') return;
    void connectTable({});
  }, delayMs);
}

/** Test seam: forget a pending rejoin. */
export function cancelRestartRejoin(): void {
  if (rejoinTimer) clearTimeout(rejoinTimer);
  rejoinTimer = null;
}
